/* global gtag, fbq */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, MapPin, Clock, Star, Users, Smartphone, Gift, Menu } from 'lucide-react';
import { ref, set } from 'firebase/database';
import { db } from '../firebaseConfig';
import { Helmet } from 'react-helmet-async';

export default function BuyChicago2() {
  const [purchaseType, setPurchaseType] = useState('self'); // 'self' or 'gift'
  const [giftTo, setGiftTo] = useState('');
  const [giftFrom, setGiftFrom] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  // Countdown timer - resets at midnight each day
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight - now;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return { hours, minutes, seconds };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Show sticky CTA on scroll
  useEffect(() => {
    const handleScroll = () => {
      // Show sticky CTA after scrolling 300px down
      setShowStickyCTA(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBuyNow = async (location) => {
    // Track the click in Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: 39.99,
        items: [{
          item_id: 'chicago-tour-premium',
          item_name: 'Chicago Spy Mission Tour (Premium)',
          price: 39.99,
          quantity: 1
        }],
        purchase_type: purchaseType,
        cta_location: location
      });
    }
    // Track in Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'InitiateCheckout', {
        content_ids: ['chicago-tour-premium'],
        content_type: 'product',
        value: 39.99,
        currency: 'USD'
      });
    }

    if (purchaseType === 'self') {
      // Regular purchase
      window.location.href = 'https://buy.stripe.com/7sYfZa3oW1QY6xF5SFcZa06?prefilled_promo_code=EXPLORE2026';
    } else {
      // Gift purchase - store personalization and redirect to gift link
      setIsProcessing(true);

      try {
        // Generate unique ID for this gift purchase intent
        const intentId = `gift_${Date.now()}_${Math.random().toString(36).substring(7)}`;

        // Store personalization data in Firebase temporarily
        await set(ref(db, `giftIntents/${intentId}`), {
          to: giftTo,
          from: giftFrom,
          message: giftMessage,
          createdAt: Date.now(),
          expiresAt: Date.now() + (60 * 60 * 1000) // Expires in 1 hour
        });

        // Redirect to gift Stripe link with intent ID
        window.location.href = `https://buy.stripe.com/3cIdR29Nk7bicW3epbcZa07?prefilled_promo_code=EXPLORE2026&client_reference_id=${intentId}`;
      } catch (error) {
        console.error('Error storing gift data:', error);
        console.error('Error details:', error.message, error.code);
        alert(`Failed to process gift purchase: ${error.message}\n\nPlease check the console for details or contact support.`);
        setIsProcessing(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Chicago Spy Mission Walking Tour - Interactive Scavenger Hunt</title>
        <meta name="description" content="Premium Chicago spy mission tour. Visit 12 landmarks, solve riddles, save Chicago's history. Enhanced experience for serious adventurers. $39.99 instant access." />
        <meta name="keywords" content="Chicago spy mission tour, premium Chicago scavenger hunt, Chicago walking adventure, Chicago riddle tour, advanced Chicago tour, things to do in Chicago" />
        <link rel="canonical" href="https://play.tourinabox.com/chicago-tour" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content="https://play.tourinabox.com/chicago-tour" />
        <meta property="og:title" content="Chicago Spy Mission - Premium Scavenger Hunt Experience" />
        <meta property="og:description" content="Premium spy mission adventure through Chicago. Visit 12 iconic landmarks, solve challenging riddles, save history. Enhanced experience for serious adventurers." />
        <meta property="og:image" content="https://play.tourinabox.com/images/chicago.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Chicago architecture and skyline - premium walking tour" />
        <meta property="og:site_name" content="Tour in a Box" />
        <meta property="og:locale" content="en_US" />
        <meta property="product:price:amount" content="39.99" />
        <meta property="product:price:currency" content="USD" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://play.tourinabox.com/chicago-tour" />
        <meta name="twitter:title" content="Chicago Spy Mission - Premium Interactive Tour" />
        <meta name="twitter:description" content="Premium spy mission through Chicago. 12 landmarks, challenging riddles, enhanced storyline. For serious adventure seekers. Instant access." />
        <meta name="twitter:image" content="https://play.tourinabox.com/images/chicago.webp" />
        <meta name="twitter:image:alt" content="Chicago architecture and skyline - premium walking tour" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": "Chicago Spy Mission Walking Tour - Premium",
            "description": "Premium interactive walking tour featuring a spy mission through Chicago. Visit 12 iconic landmarks, solve challenging riddles, and save a piece of history. Enhanced experience with deeper storyline.",
            "brand": {
              "@type": "Brand",
              "name": "Tour in a Box"
            },
            "offers": {
              "@type": "Offer",
              "url": "https://play.tourinabox.com/chicago-tour",
              "priceCurrency": "USD",
              "price": "39.99",
              "priceValidUntil": "2026-12-31",
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "Tour in a Box LLC"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "150",
              "bestRating": "5",
              "worstRating": "1"
            },
            "image": "https://play.tourinabox.com/images/chicago.webp",
            "category": "Tours & Activities"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://play.tourinabox.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Chicago Premium Tour",
                "item": "https://play.tourinabox.com/chicago-tour"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* Urgency Banner with Countdown */}
      <div className="py-2 px-4" style={{ background: '#F8662B' }}>
        <div className="flex items-center justify-center gap-3">
          <Star size={12} fill="white" style={{ color: 'white' }} />
          <span className="text-sm font-bold text-white" style={{ fontFamily: 'Rubik, sans-serif' }}>
            Sale ends in {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
          </span>
          <Star size={12} fill="white" style={{ color: 'white' }} />
          <span className="text-sm font-bold text-white" style={{ fontFamily: 'Rubik, sans-serif' }}>20% Off</span>
          <Star size={12} fill="white" style={{ color: 'white' }} />
        </div>
      </div>

      {/* Header with Logo */}
      <header className="pt-8 pb-6 px-4 border-b border-gray-200 relative" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <img
            src="/PrimaryLogo.png"
            alt="Tour in a Box"
            className="h-8 sm:h-10"
            style={{ objectFit: 'contain', width: 'auto' }}
          />

          {/* Menu Button */}
          <div className="absolute right-4 top-8">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Menu"
            >
              <Menu size={20} style={{ color: '#413C04' }} />
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                style={{ fontFamily: 'Urbanist, sans-serif' }}
              >
                <a
                  href="https://www.tourinabox.com/store"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Browse All Tours
                </a>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #FFFADF, rgba(255, 250, 223, 0.5))' }}>
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "url(/images/noise-512.png)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 sm:pt-8 sm:pb-20">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left Column - Copy */}
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Chicago, Illinois
              </h1>

              <p className="text-2xl font-bold mb-6" style={{ fontFamily: 'Rubik, sans-serif', color: '#6b8e5e' }}>
                Scavenger Hunt Tour
              </p>

              <p className="text-lg mb-6" style={{ color: '#413C04', opacity: 0.75 }}>
                Chicago's landmarks hold the clues. Crack the codes before a piece of history is stolen.
              </p>

              {/* Key Benefits */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>12 landmark stops with riddles to solve</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>1-hour self-paced adventure</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>Play instantly on web or anytime in next year</span>
                </div>
              </div>

              {/* Pricing CTA */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2" style={{ borderColor: '#E4B4A6' }}>
                <div className="flex items-baseline justify-center gap-3 mb-4">
                  <span className="text-3xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>$31.99</span>
                  <span className="text-xl text-gray-400 line-through">$40.00</span>
                  <span className="bg-red-100 text-red-700 text-sm font-bold px-2 py-1 rounded">
                    Save 20%
                  </span>
                </div>

                {/* Gift Personalization Form (conditional) */}
                {purchaseType === 'gift' && (
                  <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: '#FFFADF' }}>
                    <p className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                      <Gift size={16} />
                      Personalize Your Gift (Optional)
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                          To
                        </label>
                        <input
                          type="text"
                          value={giftTo}
                          onChange={(e) => setGiftTo(e.target.value)}
                          placeholder="Recipient's name"
                          className="w-full px-3 py-2 rounded-lg border text-sm"
                          style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                          From
                        </label>
                        <input
                          type="text"
                          value={giftFrom}
                          onChange={(e) => setGiftFrom(e.target.value)}
                          placeholder="Your name"
                          className="w-full px-3 py-2 rounded-lg border text-sm"
                          style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#385B37', fontFamily: 'Urbanist, sans-serif' }}>
                          Personal Message
                        </label>
                        <textarea
                          value={giftMessage}
                          onChange={(e) => setGiftMessage(e.target.value)}
                          placeholder="Add a personal message..."
                          rows={3}
                          maxLength={200}
                          className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
                          style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                        />
                        <p className="text-xs mt-1" style={{ color: '#385B37', opacity: 0.6, fontFamily: 'Urbanist, sans-serif' }}>
                          {giftMessage.length}/200 characters
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleBuyNow('hero')}
                  disabled={isProcessing}
                  className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: '#F8662B',
                    fontFamily: 'Rubik, sans-serif'
                  }}
                  onMouseEnter={(e) => !isProcessing && (e.currentTarget.style.background = '#E05520')}
                  onMouseLeave={(e) => !isProcessing && (e.currentTarget.style.background = '#F8662B')}
                >
                  {isProcessing ? 'Processing...' : (purchaseType === 'gift' ? '🎁 Continue to Checkout' : 'Purchase Tour')}
                </button>

                {/* Gift Toggle Link */}
                {purchaseType === 'self' ? (
                  <button
                    onClick={() => setPurchaseType('gift')}
                    className="w-full mt-3 py-3 px-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 hover:bg-gray-50"
                    style={{ borderColor: '#E4B4A6', color: '#413C04', fontFamily: 'Urbanist, sans-serif' }}
                  >
                    <Gift size={18} style={{ color: '#F8662B' }} />
                    <span className="font-semibold">Buying as a gift?</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setPurchaseType('self');
                      setGiftTo('');
                      setGiftFrom('');
                      setGiftMessage('');
                    }}
                    className="w-full text-sm mt-2 py-1 transition-all hover:underline"
                    style={{ color: '#8FB3D3', fontFamily: 'Urbanist, sans-serif' }}
                  >
                    ← Back to regular purchase
                  </button>
                )}

                <div className="mt-4 flex items-center justify-center gap-4 text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  <span className="flex items-center gap-1">
                    <Check size={16} style={{ color: '#385B37' }} />
                    1 year access
                  </span>
                  <span className="flex items-center gap-1">
                    <Check size={16} style={{ color: '#385B37' }} />
                    Instant access
                  </span>
                  <span className="flex items-center gap-1">
                    <Check size={16} style={{ color: '#385B37' }} />
                    30-day refund
                  </span>
                </div>
              </div>

              {/* Social Proof */}
              <div
                className="mt-6 flex items-center gap-4 cursor-pointer transition-opacity hover:opacity-80"
                onClick={() => {
                  document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <div className="flex -space-x-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br border-2 border-white" style={{ backgroundImage: 'linear-gradient(to bottom right, #E4B4A6, #D89E8A)' }}></div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br border-2 border-white" style={{ backgroundImage: 'linear-gradient(to bottom right, #8FB3D3, #6A92B3)' }}></div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br border-2 border-white" style={{ backgroundImage: 'linear-gradient(to bottom right, #385B37, #2A4428)' }}></div>
                </div>
                <div className="text-sm">
                  <div className="flex items-center gap-1 mb-1">
                    <div className="flex items-center gap-1" style={{ color: '#F8662B' }}>
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                    </div>
                    <span className="font-semibold" style={{ color: '#413C04' }}>4.8 out of 5</span>
                  </div>
                  <p style={{ color: '#413C04', opacity: 0.7 }}>
                    Loved by <span className="font-semibold" style={{ color: '#413C04', opacity: 1 }}>locals and visitors</span> alike
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/chicago.webp"
                  alt="Chicago architecture and skyline"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Storyline */}
      <section className="py-16 relative overflow-hidden" style={{ background: '#3d4a2d' }}>
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px'
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Rubik, sans-serif', color: '#c5d4a0' }}>
            The Mission
          </h2>

          <p className="text-lg leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            You're a spy tasked with stopping the theft of a piece of Chicago's history. Step into your mission and visit 12 of Chicago's most iconic landmarks. Each riddle unlocks a clue, and together they point to one remarkable fragment artifact hidden in plain sight.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Why Explorers Love Our Tours
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(143, 179, 211, 0.2)' }}>
                <MapPin size={24} style={{ color: '#8FB3D3' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Landmark-Based Directions</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Navigate using landmarks, not GPS. You'll look up and truly notice the city
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                <Clock size={24} style={{ color: '#385B37' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Go At Your Pace</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Pause anytime for photos, food, or shopping. Your progress is saved.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Smartphone size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Works Offline</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Download once and use without internet connection
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Star size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Engaging Riddles</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Fun challenges at each stop with hints if you need them
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(228, 180, 166, 0.4)' }}>
                <Users size={24} style={{ color: '#E4B4A6' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Perfect for Everyone</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Great for couples, families, friends, or solo adventurers
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Gift size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Great for Gifting</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Select gift option for personalized, printable gift certificate
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16" style={{ background: 'linear-gradient(to bottom right, rgba(255, 250, 223, 0.6), rgba(228, 180, 166, 0.4))' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            What Explorers Are Saying
          </h2>
          <p className="text-center mb-12" style={{ color: '#413C04', opacity: 0.7 }}>
            While our Chicago tour may be brand new, explorers love our other tours!
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "Our group loved the scavenger-hunt feel of the tour. Great idea and product. 10/10."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full" style={{ background: 'linear-gradient(to bottom right, #8FB3D3, #6A92B3)' }}></div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Verified Customer</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "We loved the little surprise at the end. I'd buy a tour for every city I visit."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full" style={{ background: 'linear-gradient(to bottom right, #385B37, #2A4428)' }}></div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Verified Customer</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "Loved using directions based on landmarks. It forced us to look up and notice the city."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full" style={{ background: 'linear-gradient(to bottom right, #F8662B, #E05520)' }}></div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Verified Customer</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Frequently Asked Questions
          </h2>

          <style>{`
            details summary {
              list-style: none;
              position: relative;
              padding-right: 2rem;
            }
            details summary::-webkit-details-marker {
              display: none;
            }
            details summary::after {
              content: '+';
              position: absolute;
              right: 0;
              top: 50%;
              transform: translateY(-50%);
              font-size: 1.5rem;
              font-weight: bold;
              color: #413C04;
              transition: transform 0.2s;
            }
            details[open] summary::after {
              content: '−';
            }
          `}</style>

          <div className="space-y-4">
            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Where does the tour start?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                The tour begins at the Art Institute of Chicago on Michigan Avenue. Once you purchase, you'll receive detailed instructions on the exact starting point.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                How long does the tour take?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                The tour typically takes about 1 hour, but you can go at your own pace! Take breaks for photos, food, or shopping. Your progress is automatically saved.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Do I need to download an app?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                No! The tour works directly in your web browser on any smartphone. You can also add it to your home screen for offline access.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                When does my access expire?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Your access is valid for 1 full year from purchase. Start the tour whenever you're ready - today, next week, or next month!
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Can multiple people use one purchase?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Each purchase unlocks access for one account. Groups or families can play together on a single device, but many prefer buying access for multiple accounts so everyone can play along on their own phone.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                What if I get stuck on a riddle?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Every location has a hint button! After one minute, you can unlock a helpful hint that will guide you toward the answer.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                What's your refund policy?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                We offer a 30-day money-back guarantee. If you're not satisfied with your tour experience, contact us for a full refund.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                What languages is this available in?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                The tour is currently available in English.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16" style={{ background: '#F8662B' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Rubik, sans-serif' }}>
            Ready for an Adventure?
          </h2>
          <p className="text-xl mb-8" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Join adventurers who've discovered Chicago in a whole new way
          </p>

          <div className="bg-white rounded-2xl p-6 max-w-md mx-auto shadow-2xl">
            <div className="flex items-baseline justify-center gap-3 mb-2">
              <span className="text-4xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>$31.99</span>
              <span className="text-2xl text-gray-400 line-through">$40.00</span>
            </div>
            <p className="text-xs text-center mb-4" style={{ color: '#413C04', opacity: 0.6 }}>
              Discount applied automatically at checkout
            </p>
            <button
              onClick={() => handleBuyNow('bottom')}
              className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg transition-all transform hover:scale-105 mb-4"
              style={{
                background: '#413C04',
                fontFamily: 'Rubik, sans-serif'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#2A2702'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#413C04'}
            >
              Purchase Tour
            </button>
            <div className="flex items-center justify-center gap-4 text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
              <span>✓ 1 year access</span>
              <span>✓ Instant access</span>
              <span>✓ 30-day refund</span>
            </div>
          </div>

          <p className="mt-6 text-sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Secure checkout powered by Stripe
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8" style={{ background: '#413C04' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm" style={{ color: 'rgba(255, 250, 223, 0.7)' }}>
              © {new Date().getFullYear()} Tour in a Box LLC
            </div>
            <div className="flex gap-6 text-sm">
              <a href="mailto:hello@tourinabox.com" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Contact
              </a>
              <Link to="/terms" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Terms
              </Link>
              <a href="https://www.tourinabox.com/policies" target="_blank" rel="noopener noreferrer" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Privacy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile-Only Sticky Bottom CTA */}
      {showStickyCTA && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50" style={{ borderColor: '#E4B4A6' }}>
          <div className="px-4 py-3">
            <button
              onClick={() => handleBuyNow('sticky')}
              className="w-full text-white font-bold py-3 px-6 rounded-xl text-base shadow-lg transition-all"
              style={{
                background: '#413C04',
                fontFamily: 'Rubik, sans-serif'
              }}
            >
              Claim This Deal — $31.99
            </button>
            <div className="flex items-center justify-center gap-3 mt-2 text-xs" style={{ color: '#413C04', opacity: 0.7 }}>
              <span>Secure checkout with Stripe</span>
              <span>•</span>
              <span>30-day refund</span>
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
}
